import { useState, useEffect, useCallback } from "react";
import { MAIN_CATEGORIES, SUBCATEGORIES } from "../types";
import { categoryLabels, subcategoryLabels } from "../lib/format";

export interface CategoryOption {
  value: string;
  label: string;
}

/**
 * Returns merged lists of categories and subcategories —
 * built-in ones plus any custom ones found in actual transaction data.
 */
export function useCategories() {
  const [usedCategories, setUsedCategories] = useState<
    Record<string, string[]>
  >({});

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/categories");
      setUsedCategories(await response.json());
    } catch {
      // Ignore category refresh errors in the UI.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Merge built-in categories with used ones
  const allCategoryKeys = new Set<string>([
    ...MAIN_CATEGORIES,
    ...Object.keys(usedCategories),
  ]);

  const allCategories: CategoryOption[] = [...allCategoryKeys].map((key) => ({
    value: key,
    label: categoryLabels[key] ?? key,
  }));

  function getSubcategories(category: string): CategoryOption[] {
    const builtIn = SUBCATEGORIES[category as keyof typeof SUBCATEGORIES] ?? [];
    const used = usedCategories[category] ?? [];
    const allKeys = new Set([...builtIn, ...used]);

    return [...allKeys].map((key) => ({
      value: key,
      label: subcategoryLabels[key] ?? key,
    }));
  }

  return { allCategories, getSubcategories, refresh };
}
